import { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ThemeContext } from 'src/Contexts/ThemeContext';
import { useAuth } from 'src/Contexts/AuthContext';
import { useToast } from 'src/Contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import i18n from 'src/i18n';
import { useBrand } from 'src/branding';

import { FiChevronLeft, FiGlobe } from 'react-icons/fi';
import { RxDashboard } from 'react-icons/rx';
import { SiStatuspal } from 'react-icons/si';
import { RiAlarmWarningLine } from 'react-icons/ri';
import { HiOutlineDocument } from 'react-icons/hi';
import { TbLogout } from 'react-icons/tb';
import { AiOutlineControl } from 'react-icons/ai';

import Nav from './NavIconTemplate';
import './Navigation.css';
import avatarIcon from '../../assets/pngwing.com.png';

const DEV_AUTO_LOGIN = import.meta.env.VITE_DEV_AUTO_LOGIN === 'true';

// Navigation component provides main sidebar navigation

const Navigation = () => {
  const [nav, setNav] = useState(false);
  const [activeTab, setActiveTab] = useState('/');
  const [language, setLanguage] = useState(i18n.language.startsWith('en') ? 'English' : 'German');

  const { t } = useTranslation();
  const brand = useBrand();

  const { DarkTheme } = useContext(ThemeContext);

  const auth = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  // Derive email directly from auth instead of storing in state (avoids setState in effect)
  const email = auth.currentUser?.name || '';

  const handleLogout = () => {
    auth.logout(); // Call the logout function from AuthContext
    navigate('/login');
  };

  const toggleLanguage = () => {
    const prevLang = i18n.language.startsWith('en') ? 'en' : 'de';
    const newLang = prevLang === 'en' ? 'de' : 'en';
    // Switch the UI optimistically, then persist the preference to the backend
    // so it follows the user on their next login / other devices.
    void i18n.changeLanguage(newLang);
    setLanguage(newLang === 'en' ? 'English' : 'German');
    auth.setPreferredLanguage(newLang).catch(() => {
      // Roll back the UI switch if the preference couldn't be saved.
      void i18n.changeLanguage(prevLang);
      setLanguage(prevLang === 'en' ? 'English' : 'German');
      toast.error(t('language.saveFailed'));
    });
  };

  //TODO Move Settings Elements to component to deduplicate the boilerplate}
  return (
    <div className={`navigation ${nav ? 'active' : ''} ${DarkTheme ? 'dark' : ''}`}>
      <div
        className={`menu ${nav ? 'active' : ''}`}
        onClick={() => setNav((prevState) => !prevState)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setNav((prevState) => !prevState);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Toggle navigation menu"
      >
        <FiChevronLeft className="menu-icon" />
      </div>
      <header>
        <Link
          to="/profile"
          className="profile"
          aria-label="Open profile"
          onClick={() => setActiveTab('/profile')}
        >
          <img src={avatarIcon} alt="User profile avatar" className="profile-img" />
        </Link>
        <span style={{ fontWeight: '500' }} aria-label="User email">
          {email}
        </span>
      </header>

      {!DEV_AUTO_LOGIN && (
        <Link
          to="/"
          style={{ textDecoration: 'none', color: 'inherit' }}
          onClick={() => setActiveTab('/')}
        >
          <Nav
            title={t('Dashboard')}
            Icon={RxDashboard}
            className={activeTab === '/' ? 'active-tab' : ''}
          />
        </Link>
      )}

      <Link
        to="/status"
        style={{ textDecoration: 'none', color: 'inherit' }}
        onClick={() => setActiveTab('/status')}
      >
        <Nav
          title={t('Status')}
          Icon={SiStatuspal}
          className={activeTab === '/status' ? 'active-tab' : ''}
        />
      </Link>

      {!DEV_AUTO_LOGIN && (
        <>
          <Link
            to="/alarmprotokoll"
            style={{ textDecoration: 'none', color: 'inherit' }}
            onClick={() => setActiveTab('/alarmprotokoll')}
          >
            <Nav
              title={t('Alarm protocol')}
              Icon={RiAlarmWarningLine}
              className={activeTab === '/alarmprotokoll' ? 'active-tab' : ''}
            />
          </Link>

          <Link
            to="/dokumente"
            style={{ textDecoration: 'none', color: 'inherit' }}
            onClick={() => setActiveTab('/dokumente')}
          >
            <Nav
              title={t('Documents')}
              Icon={HiOutlineDocument}
              className={activeTab === '/dokumente' ? 'active-tab' : ''}
            />
          </Link>
        </>
      )}

      <div
        onClick={toggleLanguage}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleLanguage();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Toggle language"
        className="language-toggle"
      >
        <Nav title={language} Icon={FiGlobe} />
      </div>

      <div className="divider"></div>

      {!DEV_AUTO_LOGIN && auth.currentUser?.is_admin && (
        <Link
          to="/management"
          style={{ textDecoration: 'none', color: 'inherit' }}
          onClick={() => setActiveTab('/management')}
        >
          <Nav
            title={t('Management')}
            Icon={AiOutlineControl}
            className={activeTab === '/management' ? 'active-tab' : ''}
          />
        </Link>
      )}

      {!DEV_AUTO_LOGIN && (
        <div className="logout-button-style">
          <Nav title={t('Logout')} Icon={TbLogout} onClick={handleLogout} />
        </div>
      )}

      {!nav && (
        <div className="sidebar-footer">
          <p>
            {t('Monitoring portal')}
            <br />
            {t('for leakage detection')}
          </p>
          <p>{brand.name}</p>
        </div>
      )}
    </div>
  );
};

export default Navigation;
